import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { get } from 'lodash-es';
import withLoader from '../../components/Loader/withLoader.js';
import constants from '../../constants.js';
import analyticsApi from '../../api/analytics.js';

// Actions
import { actions as routingActions } from '../../ducks/routing.js';
import { actions as authActions } from '../../ducks/auth.js';
import { actions as appActions } from '../../ducks/app.js';
import { actions as customerActions } from '../../ducks/customer.js';

// Components
import WorkRequestsLayout from './WorkRequestsLayout.js';
import HelpCard from '../../components/HelpCard/HelpCard.js';
import NewWorkRequestButton from './NewWorkRequestButton/NewWorkRequestButton';
import ReplaceContact from '../../components/Replace/ReplaceContact/ReplaceContact';
import TileLink from '../../components/TileLink/TileLink.js';
import BuildIcon from '@material-ui/icons/BuildOutlined';
import InfoIcon from '@material-ui/icons/InfoOutlined';

import { getString } from '../../helpers/stringHelpers.js';
import { hasRole } from '../../helpers/authHelpers.js';
import { countJobsRaisedToday } from '../../helpers/workRequestHelpers.js';
import { getLoadedStatus } from '../../helpers/loaderHelpers';
import { isFeatureDisabled } from '../../helpers/outageHelpers';

const stringsComponent = 'workRequests';

class WorkRequests extends React.Component {
  componentDidMount() {
    const { setAppBarTitle } = this.props;

    setAppBarTitle(getString(stringsComponent, 'TITLE'));
  }

  shouldComponentUpdate(nextProps) {
    // Only rerender if not in a unloaded status, or the previous status was loaded (to allow for refresh triggers)
    // This prevents the flash effect as APIs are loaded.
    if (
      getLoadedStatus(this.getLoadedStatuses(nextProps)) !== 'UNLOADED' ||
      getLoadedStatus(this.getLoadedStatuses(this.props)) === 'LOADED'
    ) {
      return true;
    }
    return false;
  }

  getLoadedStatuses(props = this.props) {
    const { tenancy, workRequests, contactDetails } = props;
    const loaded = [
      tenancy.loaded,
      workRequests.open.loaded,
      contactDetails.loaded,
    ];

    return loaded;
  }

  render() {
    const {
      signout,
      tenancy,
      workRequests,
      contactDetails,
      inOutage,
      outageFrom,
      outageTo,
      outageMessage,
      toggleContactModal,
      goWorkRequestsCreate,
      goWorkRequestsChild,
      roles,
      createdWorkRequests,
      onLoadComplete,
      goDash,
      updateContact,
      setCustomerIsolating,
    } = this.props;

    const openWorkRequestsData = get(workRequests, 'open.data', []);

    const WrappedWorkRequestsLayout = withLoader(WorkRequestsLayout);

    const createdJobsCount = createdWorkRequests.jobs.length;
    const jobsRaisedTodayCount = countJobsRaisedToday(openWorkRequestsData);

    const currentRequestsCount = openWorkRequestsData.length
      ? ` (${openWorkRequestsData.length})`
      : '';

    const loaded = this.getLoadedStatuses();

    const help = portalGuidances.find(
      (guidance) => guidance.Title === stringsComponent
    ) && (
      <HelpCard
        title={getString('help', 'MAINTENANCE_TITLE')}
        subheading={getString('help', 'MAINTENANCE_SUBHEADING')}
        content={
          portalGuidances.find(
            (guidance) => guidance.Title === stringsComponent
          ).Content
        }
        contact={
          portalGuidances.find(
            (guidance) => guidance.Title === stringsComponent
          ).Contact
        }
        faqs={
          portalGuidances.find(
            (guidance) => guidance.Title === stringsComponent
          ).FAQs
        }
        icon={<InfoIcon />}
        section={stringsComponent}
        toggleContactModal={toggleContactModal}
        isTop={false}
        gaEvent={analyticsApi.event}
        forceEnglish={true}
      />
    );

    const newRequest = (
      <NewWorkRequestButton
        content={getString(stringsComponent, 'NEW_REQUEST_CONTENT')}
        action={() => {
          goWorkRequestsCreate();
        }}
        limitReached={
          jobsRaisedTodayCount + createdJobsCount >=
          constants.DAILY_REQUEST_LIMIT
            ? getString(stringsComponent, 'NEW_REQUEST_LIMIT')
            : null
        }
        limitReached2={
          <ReplaceContact
            content={getString(stringsComponent, 'NEW_REQUEST_LIMIT_2')}
            action={toggleContactModal}
            forceEnglish={true}
            isLink={true}
          />
        }
        contactAction={toggleContactModal}
        gaEvent={() => analyticsApi.event('Maintenance', 'Click', 'Create')}
        currentRequestsAction={() => goWorkRequestsChild('current')}
        contactData={contactDetails.data.contactDetails}
        updateContact={updateContact}
        setCustomerIsolating={setCustomerIsolating}
      />
    );

    return (
      <WrappedWorkRequestsLayout
        loaded={loaded}
        hideWhileLoading={
          workRequests.open.loaded === constants.UNLOADED ||
          contactDetails.loaded === constants.UNLOADED // Contact details can be refreshed
        }
        onError={signout}
        inOutage={inOutage}
        outageFrom={outageFrom}
        outageTo={outageTo}
        outageMessage={
          outageMessage && (
            <ReplaceContact
              content={outageMessage}
              action={toggleContactModal}
              isLink={true}
            />
          )
        }
        failureText={
          <ReplaceContact
            content={portalContent.APP_ERROR_MESSAGE}
            action={toggleContactModal}
            isLink={true}
          />
        }
        notFoundText={
          <ReplaceContact
            content={portalContent.APP_NOT_FOUND_MESSAGE}
            action={toggleContactModal}
            isLink={true}
          />
        }
        help={help}
        newRequest={
          hasRole(roles, constants.ROLE_REQUEST_WRITE) &&
          !isFeatureDisabled('MAINTENANCE_WRITE', tenancy.data.area)
            ? newRequest
            : null
        }
        onLoadComplete={onLoadComplete}
        backAction={goDash}
        currentMaintenance={
          <TileLink
            action={() => goWorkRequestsChild('current')}
            title={
              getString(stringsComponent, 'OPEN_MAINTENANCE_TAB') +
              ' ' +
              currentRequestsCount
            }
            icon={BuildIcon}
          />
        }
        previousMaintenance={
          <TileLink
            action={() => goWorkRequestsChild('previous')}
            title={getString(stringsComponent, 'CLOSED_MAINTENANCE_TAB')}
            icon={BuildIcon}
          />
        }
        cancelledMaintenance={
          <TileLink
            action={() => goWorkRequestsChild('cancelled')}
            title={getString(stringsComponent, 'CANCELLED_MAINTENANCE_TAB')}
            icon={BuildIcon}
          />
        }
      />
    );
  }
}

const mapStateToProps = (state) => ({
  tenancy: state.customer.tenancy,
  workRequests: state.customer.workRequests,
  contactDetails: state.customer.contactDetails,
  createdWorkRequests: state.properties.createdWorkRequests,
  inOutage: state.app.inOutage,
  outageFrom: state.app.outageFrom,
  outageTo: state.app.outageTo,
  outageMessage: state.app.outageMessage,
  roles: state.auth.roles,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      ...routingActions,
      ...appActions,
      ...authActions,
      ...customerActions,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(WorkRequests);
